import axios from 'axios'
import { IMAGE_BLANK_DATA } from 'constants/image'
import { useNotification } from 'context/NotificationProvider'
import { useQuery } from 'context/QueryProvider'
import { useCallback, useEffect, useRef, useState } from 'react'
import { printBlob as printBlobData, triggerDownloadBlob } from 'utils/binary'
import { isStatelessResource } from 'utils/environment'
import { getFilenameFromContentDisposition } from 'utils/headers'

export function useBlob() {
  const { client } = useQuery()
  const { notifyError } = useNotification()

  const fetchBlobResponse = useCallback(
    async (url, headers, options = {}) => {
      let response
      try {
        response = await client.request(
          url,
          { plainResponse: true, responseType: 'blob', validateStatus: s => s >= 200 && s < 500, ...options },
          headers,
        )
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
          const blob = error.response.data
          try {
            error.response.data = JSON.parse(await blob.text())
          } catch (_e) {
            error.response.data = blob
          }
        }
        throw error
      }

      if (response && response.status >= 400 && response.status < 500) {
        if (response.data instanceof Blob) {
          try {
            const decodedData = await response.data.text()
            notifyError({ response: { data: JSON.parse(decodedData), status: response.status } })
          } catch (_e) {
            notifyError()
          }
          return null
        }
        notifyError(response)
        return null
      }

      return response
    },
    [client, notifyError],
  )

  const downloadBlob = useCallback(
    async (url, target, headers, options = {}) => {
      const response = await fetchBlobResponse(url, headers, options)
      if (!response?.data) return !!response

      triggerDownloadBlob(
        response.data,
        response.headers['content-type'] || 'application/octet-stream',
        getFilenameFromContentDisposition(response.headers['content-disposition']),
        target,
      )

      return true
    },
    [fetchBlobResponse],
  )

  const printBlob = useCallback(
    async (url, headers, options = {}) => {
      const response = await fetchBlobResponse(url, headers, options)
      if (!response?.data) return !!response

      printBlobData(response.data, response.headers['content-type'] || 'application/octet-stream')

      return true
    },
    [fetchBlobResponse],
  )

  return {
    fetchBlobResponse,
    downloadBlob,
    printBlob,
  }
}

export const useResourceUrl = (url, blankData = null) => {
  const { client } = useQuery()

  const [resourceUrl, setResourceUrl] = useState(blankData)
  const lastAxiosCancelSource = useRef(null)
  const lastObjectUrl = useRef(null)

  useEffect(() => {
    async function getImageBlob() {
      if (url) {
        if (isStatelessResource(url)) {
          if (lastAxiosCancelSource.current) {
            lastAxiosCancelSource.current.cancel()
          }
          const source = axios.CancelToken.source()
          lastAxiosCancelSource.current = source

          const response = await client.request(url, {
            responseType: 'arraybuffer',
            cancelToken: source.token,
            plainResponse: true,
          })

          const file = new Blob([response.data], {
            type: response.headers['content-type'] || 'application/octet-stream',
          })

          const fileUrl = window.URL.createObjectURL(file)

          if (lastObjectUrl.current) {
            // Free memory
            window.URL.revokeObjectURL(lastObjectUrl.current)
          }
          lastObjectUrl.current = fileUrl
          setResourceUrl(fileUrl)
        } else {
          setResourceUrl(url)
        }
      } else {
        // Put back initial data in case of removed url
        setResourceUrl(blankData)
      }
    }

    getImageBlob()

    return function cleanup() {
      // Cancel any fetch request
      if (lastAxiosCancelSource.current) {
        lastAxiosCancelSource.current.cancel()
      }

      if (lastObjectUrl.current) {
        // Free memory on unmount
        window.URL.revokeObjectURL(lastObjectUrl.current)
      }
    }
  }, [client, url, blankData])

  return [resourceUrl, setResourceUrl]
}

export const useImageUrl = url => useResourceUrl(url, IMAGE_BLANK_DATA)
